'use client';

/**
 * ConversationHeader (v3, design turn 6) — 56px top bar for the main column.
 * Renders in EVERY uiState now (page.tsx no longer gates it on
 * "expanded"/isExpanded — the v3 frame IS the page, there's no separate
 * landing screen without a header).
 *
 * v3 (T6/T8): the desktop HistorySidebar (rendered by page.tsx alongside
 * this header at `lg:` and up) now OWNS history browsing on desktop, so the
 * popover-based history trigger is retired at `lg:` — only the mobile
 * trigger remains, and it now opens the full-screen ConversationHistoryDrawer
 * instead of the popover. Both the left icon/logo and the center title
 * render two variants (mobile interactive / desktop static) toggled via
 * `lg:hidden` / `hidden lg:block` — avoids any JS media-query state (and the
 * SSR/hydration mismatch that would invite). The logo is dropped at `lg:`
 * entirely: the sidebar's own header already carries the brand (orb +
 * "Art of Living"), so repeating it here would crowd the 56px bar.
 *
 * Layout: left-cluster (shrink-0), title (flex-1 min-w-0 — takes residual
 * width and truncates), close (shrink-0). All three siblings are in normal
 * flex flow so the title can never overlap the left cluster on narrow
 * viewports — a regression we saw on mobile when the title was
 * `absolute left-1/2 -translate-x-1/2` and crashed into the logo at <360px.
 */

import { memo, useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronDown,
  MoreHorizontal,
  PanelLeftOpen,
  Plus,
  X,
} from 'lucide-react';
import { useConversationStore } from '@/stores/conversation-store';
import { useEmbedReturn } from '@/hooks/useEmbedReturn';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StarIcon } from './primitives/star-icon';
import { ConversationHistoryDrawer } from './conversation-history-drawer';

/**
 * One resting + one hover treatment shared by every "⋯" menu row (design
 * turn 19b). The mock draws only ONE row — "Give feedback" — and draws it
 * hovered, which is how its tint/weight originally landed on that item as
 * unconditional styling; the row then read as permanently selected. The
 * highlight belongs to `focus:`, not the base class.
 *
 * `focus:` and not `hover:` because Radix moves real DOM focus to the item
 * on pointer-over — the same reason the shadcn base class styles its own
 * highlight via `focus:bg-accent` (which twMerge drops in favour of the
 * design tint below).
 *
 * outline-none: that same base class says `outline-hidden`, which is Tailwind
 * v4 syntax and a no-op on the v3 this repo pins — so the browser's default
 * focus ring drew a black box around whichever row the pointer was over. The
 * peach tint IS the focus indicator, and it renders for keyboard traversal
 * too (Radix focuses on arrow-key move), so nothing is lost by suppressing it.
 */
const MENU_ITEM_CLASS =
  'flex items-center gap-[10px] rounded-[8px] px-[11px] py-[9px] text-[13.5px] ' +
  'text-[#4f5568] outline-none focus:bg-[#FFF3E5] focus:font-semibold focus:text-[#B0684A]';

/** Muted at rest, warm on hover — icons track their row, and the star fills
 *  only while highlighted. Set via `stroke`/`fill` rather than `text-*`: the
 *  base item class repaints descendant `color` on focus, which would fight a
 *  currentColor-driven glyph. */
const MENU_ICON_CLASS =
  'size-4 stroke-[#a99e8c] group-focus/dropdown-menu-item:stroke-[#B0684A]';

interface ConversationHeaderProps {
  /** A ?c= restore is in flight and the title isn't known yet (page.tsx:
   *  resumeBooting || expectingHistory). Shows a pulsing placeholder bar
   *  instead of falling back to "New Conversation" — that fallback read as
   *  the conversation being gone while it loaded. An in-app sidebar switch
   *  already knows the clicked row's title (resumeConversation passes it),
   *  so the real title still renders immediately in that flow. */
  restorePending?: boolean;
  /** Header "⋯" menu (feedback UI, turn 19c) — "New chat" mirrors the
   *  existing HistorySidebar/drawer reset action; only wired up when the
   *  caller passes it so a header rendered without page.tsx context (tests,
   *  future call sites) doesn't get a dead menu item. */
  onNewChat?: () => void;
  /** Opens ConversationRatingCard with source=menu (always shown — see
   *  lib/conversation-feedback-ask.ts's MENU exemption from the
   *  once-per-conversation gate). */
  onGiveFeedback?: () => void;
  className?: string;
}

export const ConversationHeader = memo(function ConversationHeader({
  restorePending = false,
  onNewChat,
  onGiveFeedback,
  className,
}: ConversationHeaderProps) {
  const router = useRouter();
  const title = useConversationStore((s) => s.activeConversationTitle);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Null unless this visit arrived from an embedded panel's expand ↗.
  const embedReturn = useEmbedReturn();

  // Same warm placeholder fill as the sidebar's HistoryListSkeleton bars.
  // Title changes crossfade (keyed on the text): a new conversation's only
  // in-place transition is "New Conversation" → the LLM-generated title
  // (the truncated-first-message placeholder is sidebar-only — see the
  // TITLE_UPDATED handler in useAssistantConnection.ts), and a sidebar
  // switch swaps to the clicked row's title. Opacity/transform ride
  // framer's inline styles, so no Bootstrap utility collision here.
  const titleNode =
    !title && restorePending ? (
      <span
        aria-hidden="true"
        className="h-[14px] w-[160px] animate-pulse rounded-[7px] bg-[#EDE4D6]"
      />
    ) : (
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={title || 'new-conversation'}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="min-w-0 truncate font-[family-name:var(--font-lora)] text-[16px] font-semibold text-[#31364e]"
        >
          {title || 'New Conversation'}
        </motion.span>
      </AnimatePresence>
    );

  return (
    <div
      role="banner"
      className={cn(
        'relative flex h-14 shrink-0 items-center gap-2 border-b bg-transparent px-[12px] sm:gap-[12px] sm:px-[20px]',
        className
      )}
      style={{ borderColor: '#f0e9df' }}
    >
      {/* Left: history trigger (mobile only — HistorySidebar replaces it at
          `lg:`) + logo (mobile only — the sidebar's own header carries the
          brand at `lg:`, so repeating it here would crowd the 56px bar) */}
      <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
        <button
          onClick={() => setDrawerOpen(true)}
          // Color transition merged with the shared press feel's transform
          // (see PRESSABLE's doc comment in design-tokens.ts).
          className="flex size-9 items-center justify-center rounded-lg text-[#31364e]/50 transition-[background-color,color,transform] duration-[160ms] ease-out hover:bg-black/5 hover:text-[#31364e] motion-safe:active:scale-[0.96] lg:hidden"
          aria-label="Conversation history"
        >
          {/* Same panel-icon family as the desktop sidebar toggle
              (PanelLeftClose/PanelLeftOpen in history-sidebar.tsx) — the old
              Clock glyph read as unrelated to history on phones (FB27 #1). */}
          <PanelLeftOpen className="size-[18px]" />
        </button>

        <Image
          src="/images/ic-logo.svg"
          alt="The Art of Living"
          width={104}
          height={44}
          className="h-7 w-auto lg:hidden"
          priority
        />

        {/* Return chip — only present for a visitor who arrived from an
            embedded panel via expand ↗ (?from=<ctx>). Expand is a round
            trip, not an exit: without this, someone who tapped it on a paid
            registration page has no route back to the page that was trying
            to convert them. Persists for the session — see useEmbedReturn.
            Hidden below `sm:` where the 56px bar has no room beside the logo
            and the browser's own back button is the closer affordance. */}
        {embedReturn && (
          <a
            href={embedReturn.url}
            className="hidden items-center gap-1.5 rounded-full border border-[#eadfce] bg-[#FBF7F1] py-1 pl-2 pr-3 text-[12.5px] font-semibold text-[#31364e] no-underline transition-colors hover:bg-[#F7F1E8] sm:inline-flex"
          >
            <ArrowLeft className="size-3.5 shrink-0 text-[#8a7c68]" />
            <span className="max-w-[168px] truncate">{embedReturn.label}</span>
          </a>
        )}
      </div>

      {/* Center: title takes residual width. flex-1 + min-w-0 lets `truncate`
          actually kick in (flex items default to min-width:auto which
          defeats text-overflow). Mobile variant opens the drawer; desktop
          variant is a static label (the sidebar owns history browsing). */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1 transition-colors hover:bg-black/[0.03] lg:hidden"
        aria-label="Open conversation history"
      >
        {titleNode}
        <ChevronDown className="size-4 shrink-0 text-[#9598a6]" />
      </button>
      <span className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
        {titleNode}
      </span>

      {/* Right: "⋯" overflow menu + close (shrink-0, 30px per design) */}
      <div className="flex shrink-0 items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex size-[30px] items-center justify-center rounded-xl text-[#31364e]/60 transition-[color,transform] duration-[160ms] ease-out hover:bg-black/5 hover:text-[#31364e] motion-safe:active:scale-[0.96]"
              aria-label="More options"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            // ring-0: the shared dropdown primitive was generated for Tailwind
            // v4 and carries `ring-1 ring-foreground/10`, but this repo pins
            // v3 (v4 isn't SCSS-compatible — see CLAUDE.md). v3 doesn't know
            // `ring-foreground/10`, so `ring-1` fell back to Tailwind's default
            // blue-500/50 and painted a blue halo outside the design's single
            // #ece3d5 hairline.
            className="w-[210px] rounded-[13px] border-[1px] border-[#ece3d5] bg-white p-[6px] shadow-[0_18px_44px_-18px_rgba(49,54,78,0.42)] ring-0"
          >
            {onNewChat && (
              <DropdownMenuItem
                onSelect={onNewChat}
                className={MENU_ITEM_CLASS}
              >
                <Plus className={MENU_ICON_CLASS} />
                New chat
              </DropdownMenuItem>
            )}
            {onGiveFeedback && (
              <DropdownMenuItem
                onSelect={onGiveFeedback}
                className={MENU_ITEM_CLASS}
              >
                <StarIcon
                  size={16}
                  className={cn(
                    MENU_ICON_CLASS,
                    'fill-none group-focus/dropdown-menu-item:fill-[#E36001] group-focus/dropdown-menu-item:stroke-[#E36001]'
                  )}
                />
                Give feedback
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          // '/us-en', not '/': deployed routing only serves /us-en/* from
          // this app — a push to '/' escapes to the global artofliving.org
          // page (the in-app root redirect never runs there).
          onClick={() => router.push('/us-en')}
          // Color transition merged with the shared press feel's transform
          // (see PRESSABLE's doc comment in design-tokens.ts).
          className="flex size-[30px] items-center justify-center rounded-xl border-[1px] border-[#e9e9e9] bg-white text-[#31364e]/60 transition-[color,transform] duration-[160ms] ease-out hover:text-[#31364e] motion-safe:active:scale-[0.96]"
          aria-label="Close assistant"
        >
          <X className="size-4" />
        </button>
      </div>

      <ConversationHistoryDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
});
